import { useState, useRef, useEffect } from "react";
import { Users, Plus, Minus, ChevronDown } from "lucide-react";
import { useApp } from "@/context/AppContext";

const MAX_ADULTS = 12;
const MAX_CHILDREN = 6;

const Stepper = ({ value, onDec, onInc, decDisabled, incDisabled, testid }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      data-testid={`${testid}-dec`}
      onClick={onDec}
      disabled={decDisabled}
      className="flex h-8 w-8 items-center justify-center rounded-none border border-sand-border text-petrol transition-colors hover:border-terracotta hover:text-terracotta disabled:cursor-not-allowed disabled:opacity-30"
    >
      <Minus size={15} />
    </button>
    <span data-testid={`${testid}-value`} className="w-6 text-center text-sm font-bold text-petrol">{value}</span>
    <button
      type="button"
      data-testid={`${testid}-inc`}
      onClick={onInc}
      disabled={incDisabled}
      className="flex h-8 w-8 items-center justify-center rounded-none border border-sand-border text-petrol transition-colors hover:border-terracotta hover:text-terracotta disabled:cursor-not-allowed disabled:opacity-30"
    >
      <Plus size={15} />
    </button>
  </div>
);

/**
 * Guests selector: adults + children + per-child age.
 * Props:
 *  - adults: number
 *  - childrenAges: number[] (each 0-17; 0 means "under 1")
 *  - onChange(adults, childrenAges)
 *  - triggerClassName: string
 *  - light: boolean (hero glass styling for the trigger)
 */
export const GuestsPicker = ({ adults = 2, childrenAges = [], onChange, triggerClassName = "", light = false }) => {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const setAdults = (n) => onChange(Math.max(1, Math.min(MAX_ADULTS, n)), childrenAges);
  const setChildrenCount = (n) => {
    const count = Math.max(0, Math.min(MAX_CHILDREN, n));
    let next = [...childrenAges];
    if (count > next.length) next = [...next, ...Array(count - next.length).fill(6)];
    else next = next.slice(0, count);
    onChange(adults, next);
  };
  const setChildAge = (idx, age) => {
    const next = [...childrenAges];
    next[idx] = age;
    onChange(adults, next);
  };

  const childCount = childrenAges.length;
  const summary = `${adults} ${adults === 1 ? t("search.guest_one") : t("search.adults").toLowerCase()}${
    childCount ? ` · ${childCount} ${childCount === 1 ? t("search.child_one") : t("search.children_many")}` : ""
  }`;

  const toggleOpen = () => {
    // Decide whether to drop the panel up or down based on available space, so it
    // never opens off-screen (e.g. the hero search bar sits at the bottom of the fold).
    if (!open) {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        const panelH = 360 + childCount * 40;
        const spaceBelow = window.innerHeight - rect.bottom;
        setFlipUp(spaceBelow < panelH && rect.top > spaceBelow);
      }
    }
    setOpen((o) => !o);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="guests-picker-trigger"
        onClick={toggleOpen}
        className={triggerClassName || `flex items-center gap-3 rounded-xl px-4 py-3 text-left ${light ? "bg-white/70" : "border border-sand-border bg-white"}`}
      >
        <Users size={18} className="shrink-0 text-terracotta" />
        <span className="flex flex-1 flex-col text-left">
          <span className="overline text-petrol/50" style={{ fontSize: "0.6rem" }}>{t("search.guests")}</span>
          <span className="text-sm font-semibold text-petrol">{summary}</span>
        </span>
        <ChevronDown size={16} className={`shrink-0 text-petrol/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="guests-picker-panel"
          className={`absolute left-0 z-[60] max-h-[70vh] w-72 overflow-y-auto rounded-none border border-sand-border bg-white p-4 shadow-2xl ${flipUp ? "bottom-full mb-2" : "mt-2"}`}
        >
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-semibold text-petrol">{t("search.adults")}</p>
              <p className="text-xs text-petrol/50">{t("search.adults_hint")}</p>
            </div>
            <Stepper
              testid="guests-adults"
              value={adults}
              onDec={() => setAdults(adults - 1)}
              onInc={() => setAdults(adults + 1)}
              decDisabled={adults <= 1}
              incDisabled={adults >= MAX_ADULTS}
            />
          </div>

          <div className="mt-1 flex items-center justify-between border-t border-sand-border py-3">
            <div>
              <p className="text-sm font-semibold text-petrol">{t("search.children")}</p>
              <p className="text-xs text-petrol/50">{t("search.children_hint")}</p>
            </div>
            <Stepper
              testid="guests-children"
              value={childCount}
              onDec={() => setChildrenCount(childCount - 1)}
              onInc={() => setChildrenCount(childCount + 1)}
              decDisabled={childCount <= 0}
              incDisabled={childCount >= MAX_CHILDREN}
            />
          </div>

          {childCount > 0 && (
            <div className="mt-1 space-y-2 border-t border-sand-border pt-3">
              {childrenAges.map((age, idx) => (
                <label key={idx} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-petrol/70">{t("search.child_age")} {idx + 1}</span>
                  <select
                    data-testid={`guests-child-age-${idx}`}
                    value={age}
                    onChange={(e) => setChildAge(idx, Number(e.target.value))}
                    className="rounded-none border border-sand-border bg-white px-2 py-1.5 text-sm font-semibold text-petrol outline-none focus:border-terracotta"
                  >
                    {Array.from({ length: 18 }).map((_, a) => (
                      <option key={a} value={a}>
                        {a === 0 ? t("search.under_one") : `${a} ${t("search.years")}`}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          )}

          <button
            type="button"
            data-testid="guests-picker-done"
            onClick={() => setOpen(false)}
            className="mt-4 w-full rounded-none bg-petrol py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terracotta"
          >
            {t("search.guests_done")}
          </button>
        </div>
      )}
    </div>
  );
};
